import React from "react";

/**
 * GoldServiceIcon — iter252 custom premium SVG icons for the
 * "خدمات أخرى" family. Bespoke, minimal-line silhouettes rendered with
 * a warm two-stop CARTAL gold gradient plus a soft radial glow, so the
 * icons feel bespoke (not off-the-shelf lucide glyphs) while staying
 * quiet enough to sit next to real content.
 *
 * Usage:
 *   <GoldServiceIcon type="hub" className="w-6 h-6" />
 *
 * types: hub | truck | care | wrench
 * All icons share a viewBox="0 0 48 48" so they're pixel-consistent in
 * whatever container size the caller picks.
 */
const GRADIENT_ID = "cartal-gold-grad";
const GLOW_ID = "cartal-gold-glow";

function GoldDefs() {
  return (
    <defs>
      <linearGradient id={GRADIENT_ID} x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#d4b56e" />
        <stop offset="55%" stopColor="#c9a55c" />
        <stop offset="100%" stopColor="#8a6f2e" />
      </linearGradient>
      <radialGradient id={GLOW_ID} cx="24" cy="24" r="22" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#c9a55c" stopOpacity="0.22" />
        <stop offset="100%" stopColor="#c9a55c" stopOpacity="0" />
      </radialGradient>
    </defs>
  );
}

function Hub({ stroke }) {
  // 3x3 quad grid — reads as "collection / more". Represents the hub
  // that ties the 3 sub-categories together.
  return (
    <>
      <circle cx="24" cy="24" r="20" fill={`url(#${GLOW_ID})`} />
      <g stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <rect x="10" y="10" width="11" height="11" rx="3" />
        <rect x="27" y="10" width="11" height="11" rx="3" />
        <rect x="10" y="27" width="11" height="11" rx="3" />
        <path d="M32.5 27 v11 M27 32.5 h11" />
      </g>
    </>
  );
}

function Truck({ stroke }) {
  return (
    <>
      <circle cx="24" cy="24" r="20" fill={`url(#${GLOW_ID})`} />
      <g stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M6 14 h20 v18 H6 z" />
        <path d="M26 20 h9 l6 6 v6 h-15 z" />
        <circle cx="15" cy="35" r="3" />
        <circle cx="33" cy="35" r="3" />
        <path d="M28 26 h7" />
      </g>
    </>
  );
}

function Care({ stroke }) {
  // Detail spray + sparkle — reads as "car care / polish"
  return (
    <>
      <circle cx="24" cy="24" r="20" fill={`url(#${GLOW_ID})`} />
      <g stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M20 12 h6 v6 h-6 z" />
        <path d="M18 18 h10 v18 a3 3 0 0 1 -3 3 h-4 a3 3 0 0 1 -3 -3 z" />
        <path d="M28 15 l4 -3 M31 20 l4 -1 M31 26 l4 1" />
        <circle cx="23" cy="27" r="2.5" />
      </g>
    </>
  );
}

function Wrench({ stroke }) {
  return (
    <>
      <circle cx="24" cy="24" r="20" fill={`url(#${GLOW_ID})`} />
      <g stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M31 8 a8 8 0 0 0 -10.5 10.5 L8 31 l6.5 6.5 12.5 -12.5 A8 8 0 0 0 37.5 14 l-4.5 4.5 -3.5 -3.5 L34 10.5 A8 8 0 0 0 31 8 z" />
        <circle cx="12" cy="34" r="1.5" fill={stroke} stroke="none" />
      </g>
    </>
  );
}

const RENDERERS = { hub: Hub, truck: Truck, care: Care, wrench: Wrench };

export default function GoldServiceIcon({ type = "hub", className = "w-6 h-6", solid = false, ...rest }) {
  const Renderer = RENDERERS[type] || Hub;
  const stroke = solid ? `url(#${GRADIENT_ID})` : `url(#${GRADIENT_ID})`;
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...rest}
    >
      <GoldDefs />
      <Renderer stroke={stroke} />
    </svg>
  );
}
